import React from "react";

export default function PrivacyPolicy() {
  const primaryColor = "#6a4ff7";
  const styles = {
    container: {
      width: "100%",
      maxWidth: "900px",
      margin: "0 auto",
      padding: "40px 20px",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: 1.8,
      backgroundColor: "#fff",
      color: "#333",
      boxSizing: "border-box", // ensures padding doesn't overflow
    },
    pageWrapper: {
      width: "100%",
      minHeight: "100vh",
      backgroundColor: "#f2f2f7", // full-width background
      display: "flex",
      justifyContent: "center",
      padding: "20px 0",
    },
    title: {
      fontSize: "38px",
      fontWeight: "bold",
      marginBottom: "20px",
      color: primaryColor,
      textAlign: "center",
    },
    subTitle: {
      fontSize: "24px",
      fontWeight: "600",
      marginTop: "40px",
      marginBottom: "15px",
      color: "#222",
      borderBottom: "2px solid #eee",
      paddingBottom: "5px",
    },
    paragraph: {
      marginBottom: "18px",
      fontSize: "16px",
      color: "#444",
      textAlign: "justify",
    },
    list: {
      marginLeft: "20px",
      marginBottom: "20px",
      paddingLeft: "15px",
      listStyleType: "disc",
      textAlign: "left",
    },
    listItem: {
      marginBottom: "10px",
      fontSize: "16px",
      lineHeight: 1.6,
    },
    smallNote: {
      fontSize: "13px",
      color: "#666",
      marginBottom: "15px",
    },
  };
  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        <h1 style={styles.title}>Privacy Policy – Quick Agent Service</h1>

        <p style={styles.paragraph}>
          <strong>Effective Date:</strong> November 30, 2025
        </p>
        <p style={styles.paragraph}>
          <strong>Last Updated:</strong> November 30, 2025
        </p>

        <p style={styles.paragraph}>
          Quick Agent Service (“Quick Agent”, “we”, “our”, “us”) values your
          privacy and is committed to protecting personal data. This Privacy
          Policy explains how we collect, use, store, share, and safeguard
          information when you access or use our mobile application, website at{" "}
          <strong>quickagent.com</strong>, and related services (collectively,
          the “Service”).
        </p>

        <p style={styles.paragraph}>
          By using the Service you acknowledge that you have read and understood
          this Privacy Policy and consent to the collection, use, transfer, and
          storage of your personal information in accordance with it. If you do
          not agree, please do not use the Service.
        </p>

        <h2 style={styles.subTitle}>Definitions & Interpretation</h2>
        <p style={styles.paragraph}>
          The words beginning with a capital letter have specific meanings in
          this Policy. “Account” means the unique username and password created
          to use the Service. “Affiliates” are entities which control, are
          controlled by, or share common control with Quick Agent. “Application”
          or “App” means the QuickAgent application. “Device” means any device
          that can access the Service (phone, tablet, computer). “Personal Data”
          or “Personal Information” means any information relating to an
          identified or identifiable individual. “Service Providers” are
          third-party companies or individuals engaged by Quick Agent to provide
          or help provide the Service.
        </p>

        <h2 style={styles.subTitle}>1. Information We Collect</h2>

        <h3 style={styles.subTitle}>1.1 Personal Information</h3>
        <p style={styles.paragraph}>
          We may collect personal information you provide directly, including:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Full name</li>
          <li style={styles.listItem}>Email address</li>
          <li style={styles.listItem}>Phone number</li>
          <li style={styles.listItem}>Address or region (optional)</li>
          <li style={styles.listItem}>
            Profile photo and other profile details
          </li>
          <li style={styles.listItem}>
            Encrypted password and account credentials
          </li>
          <li style={styles.listItem}>
            Identity documents for verification (e.g., NIN, national ID) where
            required
          </li>
        </ul>

        <h3 style={styles.subTitle}>1.2 Property & Listing Data</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            Property titles, descriptions and attributes
          </li>
          <li style={styles.listItem}>
            Photos, videos, floor plans and other media you upload
          </li>
          <li style={styles.listItem}>
            Pricing, availability and booking information
          </li>
          <li style={styles.listItem}>
            Map location and geodata associated with listings
          </li>
        </ul>

        <h3 style={styles.subTitle}>1.3 Device, Usage & Technical Data</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            Device model, operating system and version
          </li>
          <li style={styles.listItem}>
            IP address, browser type, and network information
          </li>
          <li style={styles.listItem}>
            Usage data, session logs, pages visited, and timestamps
          </li>
          <li style={styles.listItem}>
            Crash reports, performance data and diagnostic information
          </li>
        </ul>

        <h3 style={styles.subTitle}>1.4 Location & Sensor Data</h3>
        <p style={styles.paragraph}>
          With your consent, we may collect precise or approximate location data
          from your Device to provide location-based search and mapping
          features. You can disable location sharing via your Device settings at
          any time.
        </p>

        <h3 style={styles.subTitle}>1.5 Camera, Photos & Media</h3>
        <p style={styles.paragraph}>
          With your permission the App may access your camera and photo library
          to allow you to capture or upload property images and videos. This
          content may be stored on your Device and/or uploaded to Quick Agent
          servers or our Service Providers' servers to provide the Service.
        </p>

        <h3 style={styles.subTitle}>1.6 Cookies, Tracking & Analytics</h3>
        <p style={styles.paragraph}>
          We use cookies, device identifiers, web beacons and analytics tools
          (for example Google Analytics, Firebase) to understand use of the
          Service, troubleshoot issues, improve performance, and personalise
          features. See the "Cookies" section for more details.
        </p>

        <h3 style={styles.subTitle}>1.7 Payment & Transaction Data</h3>
        <p style={styles.paragraph}>
          If you transact via the Service we (or our payment processors) will
          collect payment and billing details necessary to process those
          transactions. Quick Agent does not store full payment card details
          unless explicitly stated — payment processing is performed by
          third-party processors in accordance with their privacy policies.
        </p>

        <h2 style={styles.subTitle}>2. How We Collect Data</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            Directly from you when you register, create or edit a listing,
            contact another user, or contact us.
          </li>
          <li style={styles.listItem}>
            Automatically when you use the Service via logs, cookies and similar
            technologies.
          </li>
          <li style={styles.listItem}>
            From third parties such as payment processors, identity verification
            providers, public sources, or our Affiliates.
          </li>
        </ul>

        <h2 style={styles.subTitle}>
          3. Purposes & Legal Bases for Processing
        </h2>
        <p style={styles.paragraph}>We use Personal Data to:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            Provide and maintain the Service, including account management and
            listings functionality (performance of a contract).
          </li>
          <li style={styles.listItem}>
            Communicate with you about transactions, security notices, and
            support (contractual necessity & legitimate interest).
          </li>
          <li style={styles.listItem}>
            Prevent, detect and investigate fraud, abuse and other illegal
            activity (legitimate interest & legal compliance).
          </li>
          <li style={styles.listItem}>
            Personalise recommendations, content and advertising where permitted
            (consent/legitimate interest).
          </li>
          <li style={styles.listItem}>
            Comply with legal obligations and respond to lawful requests by
            public authorities (legal obligation).
          </li>
        </ul>

        <h2 style={styles.subTitle}>4. How We Share Your Information</h2>
        <p style={styles.paragraph}>
          Quick Agent does not sell your Personal Data. We may share Personal
          Data with:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            Other users: information you disclose in public areas of the Service
            (e.g., listing contact details) may be visible to other users and
            the public.
          </li>
          <li style={styles.listItem}>
            Service Providers: hosting, data storage, payment processing,
            identity verification, messaging and analytics providers who process
            data on our behalf.
          </li>
          <li style={styles.listItem}>
            Affiliates and business partners for operational purposes or joint
            offerings.
          </li>
          <li style={styles.listItem}>
            Law enforcement, government or regulators as required by law or in
            response to valid legal process.
          </li>
          <li style={styles.listItem}>
            In connection with a business transfer: should Quick Agent merge
            with, be acquired by, or sell all or part of its assets, Personal
            Data may be transferred. We will notify you of material changes.
          </li>
          <li style={styles.listItem}>
            With your consent: other disclosures you expressly authorise.
          </li>
        </ul>

        <h2 style={styles.subTitle}>
          5. International Transfer of Personal Data
        </h2>
        <p style={styles.paragraph}>
          Your data may be processed and stored in countries other than your
          country of residence. By using the Service you consent to transfers to
          countries that may have different data protection laws. We will take
          reasonable steps to safeguard your data and, where required, implement
          appropriate transfer mechanisms (e.g., standard contractual clauses).
        </p>

        <h2 style={styles.subTitle}>6. Cookies & Similar Technologies</h2>
        <p style={styles.paragraph}>
          We use cookies and similar technologies for: authentication, security,
          preferences, analytics and advertising. You can control cookie
          settings through your browser or device settings; however disabling
          cookies may affect the functionality of the Service.
        </p>

        <h2 style={styles.subTitle}>7. Retention of Data</h2>
        <p style={styles.paragraph}>
          We retain Personal Data for as long as necessary to provide the
          Service, to comply with our legal obligations, resolve disputes, and
          enforce our agreements. Usage Data may be retained for shorter periods
          for analytics purposes. When data is no longer required we will delete
          or anonymise it in line with our retention policies.
        </p>

        <h2 style={styles.subTitle}>8. Security</h2>
        <p style={styles.paragraph}>
          We implement administrative, technical and physical measures to
          protect Personal Data (encryption in transit and at rest where
          appropriate, firewalls, restricted access, logging, and regular
          security reviews). Despite these measures, no internet transmission or
          storage can be guaranteed 100% secure.
        </p>

        <h2 style={styles.subTitle}>9. Children & Minors</h2>

        <p style={styles.listItem}>
          <strong>Under 18 (recommended):</strong> The Service is generally not
          intended for persons under 18. We recommend minors under 18 do not use
          the Service without parental or guardian supervision.
        </p>

        <h2 style={styles.subTitle}>10. Your Rights (NDPR, GDPR & Similar)</h2>
        <p style={styles.paragraph}>
          Depending on your jurisdiction, you may have rights including:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            Access to Personal Data we hold about you
          </li>
          <li style={styles.listItem}>
            Correction of inaccurate or incomplete data
          </li>
          <li style={styles.listItem}>
            Request deletion ("right to be forgotten") subject to legal
            exceptions
          </li>
          <li style={styles.listItem}>
            Request restriction or objection to processing
          </li>
          <li style={styles.listItem}>Data portability where applicable</li>
          <li style={styles.listItem}>
            Withdraw consent at any time (without affecting processing prior to
            withdrawal)
          </li>
        </ul>
        <p style={styles.paragraph}>
          To exercise these rights contact us at{" "}
          <strong>contact@quickagent.com</strong>. We may need to verify your
          identity before fulfilling requests. We aim to respond within the
          timeframes required by applicable law.
        </p>

        <h2 style={styles.subTitle}>11. How to Update or Delete Your Data</h2>
        <p style={styles.paragraph}>
          You can update or delete certain account information from within the
          App in your account settings. To request deletion of other Personal
          Data, or to request assistance, email{" "}
          <strong>contact@quickagent.com</strong>. Please note that some
          information may be retained to comply with legal obligations, resolve
          disputes, or enforce agreements.
        </p>

        <h2 style={styles.subTitle}>12. Business Transactions</h2>
        <p style={styles.paragraph}>
          In the event of a merger, acquisition, sale, or asset transfer we may
          disclose or transfer Personal Data as part of the transaction. If this
          happens, we will notify you and the acquiring entity will be required
          to honour this Privacy Policy for the data transferred, unless you are
          notified otherwise.
        </p>

        <h2 style={styles.subTitle}>13. Legal Requests & Law Enforcement</h2>
        <p style={styles.paragraph}>
          We may disclose Personal Data in response to lawful requests by public
          authorities, to comply with legal obligations, to prevent fraud or
          other illegal activities, or to protect the rights, property or safety
          of Quick Agent, our users or the public.
        </p>

        <h2 style={styles.subTitle}>14. Third-Party Services & Links</h2>
        <p style={styles.paragraph}>
          The Service may contain links to third-party websites and services.
          This Privacy Policy does not apply to such third parties. We recommend
          you review their privacy policies before providing Personal Data.
        </p>

        <h2 style={styles.subTitle}>15. Advertising & Marketing</h2>
        <p style={styles.paragraph}>
          With your consent where required, we may use Personal Data to send
          marketing communications, promotional offers and to personalise
          advertising. You can opt out of promotional messages via account
          settings or by following the opt-out instructions in the message.
        </p>

        <h2 style={styles.subTitle}>16. Changes to This Privacy Policy</h2>
        <p style={styles.paragraph}>
          We may update this Privacy Policy from time to time. When we make
          material changes, we will notify you by updating the “Last Updated”
          date above and, where appropriate, provide in-app notice or email.
          Changes take effect when posted on this page.
        </p>

        <h2 style={styles.subTitle}>17. How to Contact Us</h2>
        <p style={styles.paragraph}>
          If you have questions, requests or concerns about this Privacy Policy
          or our data practices, contact:
        </p>
        <p style={styles.paragraph}>
          <strong>Quick Agent Service</strong>
          <br />
          Website: <a href="https://quickagent.com">https://quickagentservice.com</a>
          <br />
          Email: <strong>contact@quickagent.com</strong>
        </p>

        <p style={styles.smallNote}>
          This Privacy Policy was prepared to inform you of our practices. It
          complements any agreements you might have with Quick Agent and does
          not create any contractual rights beyond those agreements.
        </p>
      </div>
    </div>
  );
}
